import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import SimpleFilter from './SimpleFilter';
import { TypeaheadSearchBar } from 'venn-components';
import { useRecoilValue } from 'recoil';
import type { MorningstarCategory } from 'venn-state';
import { allMorningstarCategoriesWithIdsSelector } from 'venn-state';
import { withSuspense } from 'venn-utils';

const fieldName = 'Morningstar category';

export const formatMorningstarCategory = (categoryName: string, categoryId: string): MorningstarCategory =>
  `${categoryName}:${categoryId}` as MorningstarCategory;

export const parseMorningstarCategory = (formatted: MorningstarCategory): { name: string; id: string } => {
  const lastColonIndex = formatted.lastIndexOf(':');
  if (lastColonIndex === -1) {
    return { name: formatted, id: formatted };
  }
  return {
    name: formatted.substring(0, lastColonIndex),
    id: formatted.substring(lastColonIndex + 1),
  };
};

interface CategoryFilterProps {
  className?: string;
  onFilter: (updated: MorningstarCategory[]) => void;
  selected: MorningstarCategory[];
  closeOnOnlyClick?: boolean;
  isAdvanced?: boolean;
}

const CategoryFilter = ({ className, selected, onFilter, closeOnOnlyClick, isAdvanced }: CategoryFilterProps) => {
  const allMorningstarCategories = useRecoilValue(allMorningstarCategoriesWithIdsSelector);
  const categories = useMemo(
    () =>
      // Note: Duplicate category names are intentionally shown separately.
      // If needed, we can later append the ID in parentheses to distinguish them: `${categoryName} (${categoryId})`
      allMorningstarCategories.map(({ categoryName, categoryId }) => ({
        label: categoryName,
        value: formatMorningstarCategory(categoryName, categoryId),
        tooltip: `Morningstar Category Code: ${categoryId}`,
      })),
    [allMorningstarCategories],
  );

  const handleFilter = useCallback(
    (updated: string | string[]) => {
      const updatedCategories = (Array.isArray(updated) ? updated : [updated]) as MorningstarCategory[];
      onFilter(updatedCategories);
    },
    [onFilter],
  );
  const [textFilter, setTextFilter] = useState<string>('');
  const searchComponent = useMemo(() => {
    return (
      <SearchBarContainer>
        <TypeaheadSearchBar
          disableAutofocus
          placeholder="Search Morningstar"
          value={textFilter}
          onChange={setTextFilter}
        />
      </SearchBarContainer>
    );
  }, [textFilter, setTextFilter]);

  return (
    <StyledFilter
      header={searchComponent}
      menuWidth={400}
      className={className}
      label={fieldName}
      items={categories}
      initialSelected={selected}
      onFilter={handleFilter}
      closeOnOnlyClick={closeOnOnlyClick}
      isAdvanced={isAdvanced}
      textFilter={textFilter}
    />
  );
};

const StyledFilter = styled(SimpleFilter)`
  display: inline-flex;
`;

const SearchBarContainer = styled.div`
  padding: 4px;
`;

export default withSuspense(null, CategoryFilter);
